import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDButton from "components/MDButton";
import { getJobs } from "services/api";
import { getScenarios } from "services/api";
import { getScenarioJobs } from "services/api";
import { useAuth } from "context/authContext";

function AddScenarioJobDialog({ open, onClose, onSave, scenarioId, jobs, job, onDelete }) {
  const { t } = useTranslation();
  const { employee } = useAuth();

  const [jobName, setJobName] = useState("");
  const [numberOfEmployees, setNumberOfEmployees] = useState("");
  const [salary, setSalary] = useState("");
  const [reportingToSolid, setReportingToSolid] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // --- NEW STATE FOR CLONING ---
  const [creationMode, setCreationMode] = useState("manual"); // 'manual' | 'clone'
  const [sourceType, setSourceType] = useState("COMPANY"); // 'COMPANY' | 'SCENARIO'

  // Lists for dropdowns
  const [sourceScenarios, setSourceScenarios] = useState([]);
  const [availableSourceJobs, setAvailableSourceJobs] = useState([]);

  // Selections
  const [selectedSourceScenarioId, setSelectedSourceScenarioId] = useState(null);
  const [selectedSourceJobId, setSelectedSourceJobId] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [scenariosLoading, setScenariosLoading] = useState(false);

  const validateJob = (checkDuplicate, isDelete) => {
    let errors = {};

    // When deleting — check if any job reports to this one
    if (isDelete && job) {
      const hasSubordinates = jobs?.some((j) => j.reportingToSolid === job.id);
      if (hasSubordinates) {
        errors.reportingToSolid = t("user.jobs.hasSubordinatesError"); // you can add this key in your i18n file
        return errors; // stop here since deletion isn't allowed
      }
    }

    // Required: Job Name
    if (!isDelete && !jobName) {
      errors.jobName = t("user.jobs.validationRequired");
    }

    // Required: Reporting To (only if there are other jobs and no valid reportingToSolid)
    if (
      !isDelete &&
      jobs?.length > 0 &&
      !reportingToSolid &&
      (!job || job?.reportingToSolid != null)
    ) {
      errors.reportingToSolid = t("user.jobs.validationRequired");
    }

    // Duplicate name check (only for create/edit, not delete)
    if (!isDelete && checkDuplicate) {
      const duplicate = jobs?.some(
        (j) => j.jobName.toLowerCase() === jobName.toLowerCase() && j.id !== (job?.id ?? null)
      );
      if (duplicate) {
        errors.jobName = t("user.jobs.validationUnique");
      }
    }

    // Circular loop check
    if (!isDelete && job && createsCycle(jobs, job.id, reportingToSolid)) {
      errors.reportingToSolid = t("user.jobs.reportingLoopError");
    }

    return errors;
  };

  // --- INITIALIZATION ---
  useEffect(() => {
    if (job) {
      // Edit Mode
      setJobName(job.jobName || "");
      setReportingToSolid(job.reportingToSolid || "");
      setNumberOfEmployees(job.numberOfEmployees || "");
      setSalary(job.salary || "");
      setCreationMode("manual"); // Force manual when editing
    } else {
      // Add Mode - Reset
      setJobName("");
      setReportingToSolid("");
      setNumberOfEmployees("");
      setSalary("");
      setCreationMode("manual");
      setSelectedSourceJobId(null);
    }
    setErrors({});
  }, [job, open]);

  // --- FETCHING SOURCE DATA LOGIC ---
  useEffect(() => {
    if (!open || creationMode !== "clone") return;

    const fetchSourceData = async () => {
      try {
        // 1. CLEAR DATA IMMEDIATELY to prevent seeing old data while loading
        setAvailableSourceJobs([]);
        setSelectedSourceJobId(null);
        setSelectedSourceScenarioId(null);

        if (sourceType === "COMPANY") {
          // Case A: Fetching Company Jobs directly
          setJobsLoading(true); // <--- Use jobsLoading here
          const res = await getJobs();
          setAvailableSourceJobs(res);
          setJobsLoading(false);
        } else if (sourceType === "SCENARIO") {
          // Case B: Fetching List of Scenarios
          setScenariosLoading(true);
          const res = await getScenarios(employee.id);
          setSourceScenarios(res.filter((s) => s.id !== scenarioId));
          setScenariosLoading(false);
        }
      } catch (e) {
        console.error(e);
        setJobsLoading(false);
        setScenariosLoading(false);
      }
    };
    fetchSourceData();
  }, [open, creationMode, sourceType, scenarioId]); // Dependencies

  // --- FETCH SCENARIO JOBS LOGIC ---
  useEffect(() => {
    if (sourceType === "SCENARIO" && selectedSourceScenarioId) {
      const fetchScenarioJobs = async () => {
        // Clear previous jobs immediately
        setAvailableSourceJobs([]);
        setSelectedSourceJobId(null);

        setJobsLoading(true);
        try {
          const res = await getScenarioJobs(selectedSourceScenarioId);
          setAvailableSourceJobs(res);
        } catch (e) {
          console.error(e);
        } finally {
          setJobsLoading(false);
        }
      };
      fetchScenarioJobs();
    }
  }, [selectedSourceScenarioId, sourceType]);

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) setCreationMode(newMode);
  };

  const handleSaveInternal = async () => {
    setErrors({});

    // 1. Common Validation
    // For cloning, we assume jobName comes from source, but we still need Reporting To
    // unless you want to allow root nodes.
    const newErrors = {};
    if (creationMode === "manual" && !jobName.trim()) newErrors.jobName = t("Required");
    // Reporting To might be optional if it's a root node, but based on your code it looks required
    if (!reportingToSolid && jobs.length > 0) newErrors.reportingToSolid = t("Required");

    if (creationMode === "clone") {
      if (!selectedSourceJobId) newErrors.sourceJob = t("Required");
      if (sourceType === "SCENARIO" && !selectedSourceScenarioId)
        newErrors.sourceScenario = t("Required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (creationMode === "manual") {
        const isEdit = Boolean(job);

        const newErrors = validateJob(true, false);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setLoading(true);

        const payload = {
          jobName,
          salary: salary ? Number(salary) : null,
          numberOfEmployees: numberOfEmployees ? Number(numberOfEmployees) : 0,
          reportingToSolid: reportingToSolid ? Number(reportingToSolid) : null,
        };

        // Only include scenarioId in CREATE mode
        if (!isEdit) payload.scenarioId = scenarioId;

        // For edit, include only id + the 4 fields (no scenarioId)
        const jobPayload = isEdit ? { id: job.id, ...payload } : payload;

        await onSave(jobPayload, true);

        setLoading(false);
        onClose();
      } else {
        // --- CLONE LOGIC ---
        // Clean salary string (remove commas)
        const cleanSalary = salary ? salary.replace(/,/g, "") : null;

        // 1. Construct Payload matching 'cloneJobforScenario' arguments
        const clonePayload = {
          targetScenarioId: scenarioId,
          sourceJobId: selectedSourceJobId,
          sourceType: sourceType, // "COMPANY" or "SCENARIO"
          targetManagerId: reportingToSolid ? Number(reportingToSolid) : null,
          numberOfEmployees: numberOfEmployees ? Number(numberOfEmployees) : null,
          salary: cleanSalary ? Number(cleanSalary) : null,
        };

        // 2. Call onSave with (data, outerDialog=true, isClone=true)
        // We pass 'true' for isClone so the parent knows which API to call
        if (onSave) await onSave(clonePayload, true, true);
      }
      onClose();
    } catch (error) {
      console.error(error);
      // Handle error (show snackbar etc)
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const isEdit = Boolean(job);

    const newErrors = validateJob(true, false);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    const payload = {
      jobName,
      salary: salary ? Number(salary) : null,
      numberOfEmployees: numberOfEmployees ? Number(numberOfEmployees) : 0,
      reportingToSolid: reportingToSolid ? Number(reportingToSolid) : null,
    };

    // Only include scenarioId in CREATE mode
    if (!isEdit) payload.scenarioId = scenarioId;

    // For edit, include only id + the 4 fields (no scenarioId)
    const jobPayload = isEdit ? { id: job.id, ...payload } : payload;

    await onSave(jobPayload, true);

    setLoading(false);
    onClose();
  };

  const handleDelete = async () => {
    const newErrors = validateJob(false, true);

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    await onDelete(job?.id);
    setLoading(false);
    onClose();
  };

  function createsCycle(jobs, selectedJobId, newReportingToId) {
    let current = newReportingToId;

    while (current != null) {
      if (current === selectedJobId) {
        return true; // cycle detected
      }
      const parent = jobs.find((j) => j.id === current)?.reportingToSolid;
      current = parent ?? null;
    }

    return false;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{job ? t("user.scenarios.editJob") : t("user.scenarios.addJob")}</DialogTitle>

      <DialogContent>
        {/* Toggle Mode (Only visible when Adding New) */}
        {!job && (
          <Box display="flex" justifyContent="center" mb={2}>
            <ToggleButtonGroup
              value={creationMode}
              exclusive
              onChange={handleModeChange}
              size="small"
              color="primary"
            >
              <ToggleButton value="manual">{t("user.scenarios.manualCreation")}</ToggleButton>
              <ToggleButton value="clone">{t("user.scenarios.cloneFromExisting")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {/* ================= CLONE MODE FORM ================= */}
        {!job && creationMode === "clone" && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f8f9fa", borderRadius: 2 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="textSecondary"
              mb={1}
              display="block"
            >
              {t("user.scenarios.sourceSelection")} {/* 👈 Updated from Hardcoded text */}
            </Typography>

            {/* 1. Source Type Switch */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography
                variant="body2"
                fontWeight={sourceType === "COMPANY" ? "bold" : "regular"}
                color={sourceType === "COMPANY" ? "primary" : "text.secondary"}
                onClick={() => setSourceType("COMPANY")}
                sx={{ cursor: "pointer" }}
              >
                {t("user.scenarios.companyStructure")}
              </Typography>

              <Switch
                checked={sourceType === "SCENARIO"}
                onChange={(e) => {
                  const newType = e.target.checked ? "SCENARIO" : "COMPANY";
                  setSourceType(newType);
                  setAvailableSourceJobs([]);
                  setSelectedSourceJobId(null);
                  setSelectedSourceScenarioId(null);
                }}
                color="info"
              />

              <Typography
                variant="body2"
                fontWeight={sourceType === "SCENARIO" ? "bold" : "regular"}
                color={sourceType === "SCENARIO" ? "primary" : "text.secondary"}
                onClick={() => setSourceType("SCENARIO")}
                sx={{ cursor: "pointer" }}
              >
                {t("user.scenarios.anotherScenario")}
              </Typography>
            </Box>

            {/* 2. Select Scenario */}
            {sourceType === "COMPANY" && (
              <Autocomplete
                options={availableSourceJobs}
                getOptionLabel={(opt) => opt.jobName || ""}
                loading={jobsLoading}
                value={
                  availableSourceJobs.find((j) => (j.id || j.jobId) === selectedSourceJobId) || null
                }
                onChange={(_, val) => setSelectedSourceJobId(val?.id || val?.jobId)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("user.scenarios.selectJobToClone")}
                    margin="normal"
                    required
                    error={!!errors.sourceJob}
                    helperText={errors.sourceJob}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {jobsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {/* ================= CASE B: SCENARIO SOURCE ================= */}
            {sourceType === "SCENARIO" && (
              <>
                {/* 1. Select Scenario */}
                <Autocomplete
                  options={sourceScenarios}
                  getOptionLabel={(opt) => opt.scenarioName || ""}
                  onChange={(_, val) => setSelectedSourceScenarioId(val?.id)}
                  loading={scenariosLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.scenarios.selectSourceScenario")}
                      margin="normal"
                      error={!!errors.sourceScenario}
                      helperText={errors.sourceScenario}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {scenariosLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* 2. Select Job (Only appears after scenario is selected) */}
                <Autocomplete
                  options={availableSourceJobs}
                  getOptionLabel={(opt) => opt.jobName || ""}
                  loading={jobsLoading}
                  // Disable if no scenario selected yet
                  disabled={!selectedSourceScenarioId}
                  value={
                    availableSourceJobs.find((j) => (j.id || j.jobId) === selectedSourceJobId) ||
                    null
                  }
                  onChange={(_, val) => setSelectedSourceJobId(val?.id || val?.jobId)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.scenarios.selectJobToClone")}
                      margin="normal"
                      required
                      error={!!errors.sourceJob}
                      helperText={errors.sourceJob}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {jobsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* ================= MANUAL MODE FORM (Or Overrides for Clone) ================= */}

        {/* Job Name (Only for Manual - Clone gets it from source) */}
        {creationMode === "manual" && (
          <TextField
            fullWidth
            margin="normal"
            label={t("user.jobs.jobName")}
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            required
            error={!!errors.jobName}
            helperText={errors.jobName}
          />
        )}

        {/* Reporting To (Required for Both Modes) */}
        {jobs?.length > 0 && (
          <Autocomplete
            options={jobs}
            getOptionLabel={(option) => option.jobName}
            value={jobs.find((j) => j.id === reportingToSolid) || null}
            onChange={(_, newValue) => {
              setReportingToSolid(newValue ? newValue.id : "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                label={t("user.jobs.reportingTo")}
                required
                error={!!errors.reportingToSolid}
                helperText={errors.reportingToSolid}
              />
            )}
          />
        )}

        {/* Overrides (Optional for Clone, Input for Manual) */}
        <Box mt={creationMode === "clone" ? 2 : 0}>
          <TextField
            fullWidth
            margin="normal"
            label={t("user.jobs.numberOfEmployees")}
            type="number"
            inputProps={{ min: 0 }}
            value={numberOfEmployees}
            onChange={(e) => setNumberOfEmployees(e.target.value)}
            placeholder={creationMode === "clone" ? t("user.scenarios.leaveEmpty") : ""}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t("user.jobs.salary")}
            type="text"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            value={salary === "" || salary == null ? "" : Number(salary).toLocaleString()}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/,/g, "");
              if (rawValue === "" || (/^\d+$/.test(rawValue) && Number(rawValue) >= 0)) {
                setSalary(rawValue);
              }
            }}
            placeholder={creationMode === "clone" ? t("user.scenarios.leaveEmpty") : ""}
          />
        </Box>
      </DialogContent>

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogActions>
          <MDButton onClick={onClose}>{t("admin.areas.cancel")}</MDButton>
          {job && (
            <Button
              onClick={onDelete}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.employees.employeeDialog.buttons.delete")}
            </Button>
          )}
          <MDButton color="info" onClick={handleSaveInternal} variant="contained">
            {creationMode === "clone" ? t("user.scenarios.cloneJob") : t("admin.areas.save")}
          </MDButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

AddScenarioJobDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  scenarioId: PropTypes.number.isRequired,
  jobs: PropTypes.array.isRequired,
  job: PropTypes.object,
  onDelete: PropTypes.func,
};

export default AddScenarioJobDialog;
